/**
 * TerminalsTab — Equipos del punto de venta.
 *
 * Activar y revocar piden `pos.terminals: approve`, no `edit`: entregan o quitan
 * la capacidad de emitir a nombre de la empresa (PLAN_POS_IMPLEMENTACION §2.1).
 */
import { useEffect, useState } from 'react'
import { Plus, Pencil, KeyRound, Ban } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { POS_TERMINAL_STATUS } from '@/components/ds/statusCatalogs'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage, extractFieldErrors } from '@/lib/errorUtils'
import { fmtDateTime } from '@/lib/utils'
import { CLASES, TIPOS_POR_CLASE, nombreDelTipo, type ClaseDeTerminal } from '../tiposDeDocumento'
import { estadoDelEquipo, type Tono } from '../estadoDelEquipo'
import {
  usePosStores, usePosTerminals, useSavePosTerminal, useActivationCode, useRevokeTerminal,
  type PosTerminal, type ActivationCode,
} from '../hooks/usePos'

export default function TerminalsTab() {
  const { can } = usePermission()
  const toast = useToast()
  const [editing, setEditing] = useState<Partial<PosTerminal> | null>(null)
  const [codigo, setCodigo] = useState<{ terminal: PosTerminal; code: ActivationCode; previo: string | null } | null>(null)
  // Quien generó el código suele quedarse mirando la pantalla hasta que la caja
  // lo ingresa: sin refrescar, el terminal seguía «Sin activar» hasta recargar.
  const { data: terminals = [], isLoading } = usePosTerminals(codigo ? 5000 : 60000)
  const { data: stores = [] } = usePosStores()
  // Se compara contra el `activated_at` que tenía al generar el código, no contra
  // el reloj del navegador, que puede no coincidir con el del servidor.
  const activadoAhora = codigo && terminals.some((t) => t.id === codigo.terminal.id
    && !!t.activated_at && t.activated_at !== codigo.previo)
  const { mutate: generar, isPending: generando } = useActivationCode()
  const { mutateAsync: revocar } = useRevokeTerminal()
  const { confirm, modalProps } = useConfirmModal()
  const puedeActivar = can('pos.terminals', 'approve')

  const pedirCodigo = (t: PosTerminal) => {
    generar(t.id, {
      onSuccess: (code) => setCodigo({ terminal: t, code, previo: t.activated_at }),
      onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo generar el código.')),
    })
  }

  const pedirRevocacion = (t: PosTerminal) => confirm({
    message: `¿Revocar ${t.code}? El equipo deja de emitir en su siguiente conexión y hay que activarlo de nuevo para volver a usarlo.`,
    confirmLabel: 'Revocar',
    onConfirm: async () => {
      try {
        await revocar(t.id)
        toast.success(`${t.code} revocado`)
      } catch (e) {
        toast.error(extractErrorMessage(e, 'No se pudo revocar el terminal.'))
      }
    },
  })

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-content-muted">{terminals.length} terminales</span>
        {can('pos.terminals', 'create') && (
          <Button onClick={() => setEditing({ enabled_document_types: [39], mode: 'retail', kind: 'pos', is_active: true })}
            disabled={stores.length === 0} title={stores.length === 0 ? 'Primero hay que crear un local.' : undefined}>
            <Plus size={14} /> Nuevo terminal
          </Button>
        )}
      </div>

      <DataTable<PosTerminal>
        rows={terminals}
        rowKey={(t) => t.id}
        loading={isLoading}
        empty={<EmptyState title="No hay terminales." hint="Un terminal es una caja del POS. Se crea acá y se activa en el equipo con un código de un solo uso." size="sm" />}
        columns={[
          { key: 'code', header: 'Código', render: (t) => <span className="font-mono">{t.code}</span> },
          { key: 'name', header: 'Nombre' },
          { key: 'kind', header: 'Clase', render: (t) => (t.kind === 'agente' ? 'Clobi Conector' : 'Punto de venta') },
          { key: 'store', header: 'Local', render: (t) => t.store_name },
          {
            key: 'docs', header: 'Documentos',
            render: (t) => t.enabled_document_types.join(', '),
          },
          { key: 'status', header: 'Estado', render: (t) => <StatusBadge status={t.status} catalog={POS_TERMINAL_STATUS} /> },
          {
            key: 'seen', header: 'Último contacto',
            render: (t) => <UltimoContacto terminal={t} />,
          },
          {
            key: 'actions', header: '',
            render: (t) => (
              <div className="flex items-center justify-end gap-1">
                {puedeActivar && t.status !== 'inactive' && (
                  <button onClick={() => pedirCodigo(t)} disabled={generando}
                    title={t.status === 'active' ? 'Activar en otro equipo (revoca el actual)' : 'Generar código de activación'}
                    aria-label={`Código de activación de ${t.code}`}
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-primary">
                    <KeyRound size={14} />
                  </button>
                )}
                {puedeActivar && t.status === 'active' && (
                  <button onClick={() => pedirRevocacion(t)} title="Revocar" aria-label={`Revocar ${t.code}`}
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-danger-content">
                    <Ban size={14} />
                  </button>
                )}
                {can('pos.terminals', 'edit') && (
                  <button onClick={() => setEditing(t)} title="Editar" aria-label={`Editar ${t.code}`}
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            ),
          },
        ]}
      />

      {editing && (
        <TerminalModal
          initial={editing}
          stores={stores.filter((s) => s.is_active || s.id === editing.store)}
          onClose={() => setEditing(null)}
          onSaved={(nuevo) => {
            toast.success(nuevo ? 'Terminal creado' : 'Terminal actualizado')
            setEditing(null)
          }}
        />
      )}

      {codigo && (
        <Modal
          open
          onClose={() => setCodigo(null)}
          title={`Activar ${codigo.terminal.code}`}
          size="sm"
          actions={<Button onClick={() => setCodigo(null)}>Listo</Button>}
        >
          <p className="text-sm text-content-muted mb-4">
            Ingresar este código en el POS. Sirve una sola vez y vence a las{' '}
            <strong className="text-content">
              {new Date(codigo.code.expires_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
            </strong>{' '}(15 minutos).
          </p>
          <p className="text-center font-mono text-3xl tracking-[0.3em] text-content select-all py-3 rounded-lg bg-surface-muted"
            aria-label="Código de activación">
            {codigo.code.code.slice(0, 4)}-{codigo.code.code.slice(4)}
          </p>
          <p className="text-xs text-content-subtle mt-4">
            No se puede volver a ver: si se pierde, se genera otro y el anterior deja de servir.
            {codigo.terminal.status === 'active' && ' Al activarlo, el equipo que lo usa hoy queda revocado.'}
          </p>
          <p role="status" className={`text-sm mt-3 font-medium ${activadoAhora ? 'text-success-content' : 'text-content-muted'}`}>
            {activadoAhora ? 'El equipo quedó activado.' : 'Esperando que el equipo ingrese el código…'}
          </p>
        </Modal>
      )}

      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function TerminalModal({ initial, stores, onClose, onSaved }: {
  initial: Partial<PosTerminal>
  stores: { id: string; code: string; name: string }[]
  onClose: () => void
  onSaved: (nuevo: boolean) => void
}) {
  const { register, handleSubmit, reset, setError, watch, setValue, formState: { errors } } =
    useForm<Partial<PosTerminal>>()
  const { mutate, isPending } = useSavePosTerminal()
  const [error, setGeneral] = useState<string | null>(null)
  const tipos = watch('enabled_document_types') || []
  const clase = (watch('kind') || 'pos') as ClaseDeTerminal

  useEffect(() => { reset(initial) }, [initial, reset])

  // Al cambiar de clase quedan solo los tipos que esa clase emite.
  const cambiarClase = (nueva: ClaseDeTerminal) => {
    setValue('kind', nueva)
    setValue('enabled_document_types', tipos.filter((c) => TIPOS_POR_CLASE[nueva].includes(c)))
  }

  const alternar = (code: number) => setValue(
    'enabled_document_types',
    tipos.includes(code) ? tipos.filter((c) => c !== code) : [...tipos, code],
  )

  const onSubmit = (data: Partial<PosTerminal>) => {
    setGeneral(null)
    if (!data.enabled_document_types?.length) {
      setGeneral('Hay que habilitar al menos un tipo de documento.')
      return
    }
    mutate({ ...data, id: initial.id }, {
      onSuccess: () => onSaved(!initial.id),
      onError: (e) => {
        const campos = extractFieldErrors(e)
        if (campos) Object.entries(campos).forEach(([k, m]) => setError(k as keyof PosTerminal, { message: m }))
        setGeneral(extractErrorMessage(e, 'No se pudo guardar el terminal.'))
      },
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={initial.id ? `Editar ${initial.code}` : 'Nuevo terminal'}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="pos-terminal-form" isPending={isPending}>Guardar</Button>
        </>
      }
    >
      <form id="pos-terminal-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField dense label="Código" required placeholder="CAJA-01"
            inputProps={register('code', { required: 'El código es obligatorio.' })} error={errors.code?.message} />
          <TextField dense label="Nombre" placeholder="Caja 1" inputProps={register('name')} error={errors.name?.message} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField dense label="Local" required placeholder="Seleccionar el local"
            options={stores.map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
            selectProps={register('store', { required: 'El local es obligatorio.' })}
            error={errors.store?.message} />
          <SelectField dense label="Clase" options={CLASES} value={clase}
            onChange={(e) => cambiarClase(e.target.value as ClaseDeTerminal)}
            disabled={!!initial.id}
            hint={initial.id
              ? 'La clase no se cambia: sus credenciales y folios son de esa clase.'
              : clase === 'agente'
                ? 'Recibe los documentos que genera el sistema de gestión de la empresa y los imprime en el equipo.'
                : undefined} />
        </div>
        {clase === 'pos' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField dense label="Modo"
              options={[{ value: 'retail', label: 'Retail' }, { value: 'gastronomia', label: 'Gastronomía' }]}
              selectProps={register('mode')} />
          </div>
        )}
        <fieldset>
          <legend className="cl-label">Documentos que emite</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-1">
            {TIPOS_POR_CLASE[clase].map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm text-content">
                <input type="checkbox" checked={tipos.includes(code)} onChange={() => alternar(code)} />
                {nombreDelTipo(code)}
              </label>
            ))}
          </div>
        </fieldset>
        {initial.id && (
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" {...register('is_active')} />
            Activo. Desactivado, el equipo no puede operar aunque tenga credencial.
          </label>
        )}
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </form>
    </Modal>
  )
}

const TONO: Record<Tono, string> = {
  success: 'text-success-content',
  warning: 'text-warning-content',
  danger: 'text-danger-content',
  muted: 'text-content-subtle',
}

/** Último contacto y, para Clobi Conector, lo que dijo su último latido: conexión, cola y reloj. */
function UltimoContacto({ terminal: t }: { terminal: PosTerminal }) {
  if (!t.last_seen_at) return <span className="text-content-subtle">Nunca</span>
  const { conexion, avisos, detalle } = estadoDelEquipo(t)
  const titulo = detalle || (t.app_version ? `Versión ${t.app_version}` : undefined)
  return (
    <div title={titulo} className="leading-tight">
      <div>
        {conexion && <span className={`${TONO[conexion.tono]} font-medium`}>{conexion.texto} · </span>}
        {fmtDateTime(conexion ? t.heartbeat_at : t.last_seen_at)}
      </div>
      {avisos.length > 0 && (
        <div className="text-xs">
          {avisos.map((a, i) => (
            <span key={a.texto} className={TONO[a.tono]}>{i > 0 && ' · '}{a.texto}</span>
          ))}
        </div>
      )}
    </div>
  )
}
